import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import {
  IssuerAddCashMovementModal,
  mapCashMovementTypeToText,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddCashMovementModal.tsx';
import { IssuerCashMovementFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerCashMovementFullInfoModal.tsx';
import { mapPortfolioTransactionType } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import { mapStatusToPlusMinusType } from '@/modules/issuer-dashboard/components/portfolios/mapStatusToPlusMinusType.ts';
import { useIssuerPortfolioCashMovementInfiniteListQuery } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  FilterItem,
  ModalProviderContext,
  SelectOption,
  SelectPaginateOption,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { fixNameEnum, goodFormatNumberCustom } from 'ui/utils';
import {
  DividendStatus,
  GetPortfolioCashMovementListV1SelfCustodyCashMovementGetParams,
  PaymentStatus,
  PortfolioCashMovementPaginateResponse,
  RegisterBankExtended,
  TransactionStatus,
} from 'api';

type TypeHead = 'ID' | 'Status' | 'Type' | 'Sender' | 'Destination' | 'Total' | 'Date' | '';

const headItems: TableHeadColumn<TypeHead>[] = [
  { title: 'ID' },
  { title: 'Status' },
  { title: 'Type' },
  { title: 'Sender' },
  {
    title: 'Destination',
  },
  {
    title: 'Total',
  },
  {
    title: 'Date',
    className: 'min-w-[140px]',
  },
];

export type CashMovementsTabFilters = Omit<
  GetPortfolioCashMovementListV1SelfCustodyCashMovementGetParams,
  'page_size' | 'page' | 'sorted_by'
>;

export interface CashMovementsTabProps {
  portfolioId?: number;
}

export const CashMovementsTab = ({ portfolioId }: CashMovementsTabProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { openModal } = useContext(ModalProviderContext);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { isMobile } = useIsMobile();
  const [filters, setFilters] = useState<CashMovementsTabFilters>({
    portfolio_id: portfolioId,
  });
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const {
    data: issuerPaymentsList,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetching,
  } = useIssuerPortfolioCashMovementInfiniteListQuery(
    {
      page: 1,
      page_size: 30,
      ...filters,
      register_id: Number(registerId),
    },
    false,
  );

  const handleOpenOperationInfoModal = useCallback(
    (item: PortfolioCashMovementPaginateResponse) => {
      openModal(IssuerCashMovementFullInfoModal, {
        item,
        status: item.status || item?.transaction?.status || item?.dividend_event?.status || '',
        registerId,
        registerCountry,
      });
    },
    [openModal, registerCountry, registerId],
  );

  const handleGoToHolderPage = useCallback(
    (entityId?: number) => {
      if (!entityId) return;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(entityId.toString()),
          search: params.toString(),
        },
        { replace: false, state: { from: pathname + '?' + params.toString() } },
      );
    },
    [navigate, params, pathname],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerPaymentsList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerPaymentsList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          const currency =
            data?.to_bank?.bank_currency?.toUpperCase() ||
            data?.from_bank?.bank_currency?.toUpperCase();

          const body: BodyCell[] = [
            {
              cell: (
                <div className="flex items-center gap-3">
                  <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">
                    #{data.id}
                  </span>
                </div>
              ),
            },
            {
              cell: (
                <ActivityStatusBadge
                  status={
                    data.status || data?.transaction?.status || data?.dividend_event?.status || ''
                  }
                />
              ),
            },
            {
              cell: (
                <span className="flex gap-2 items-center">
                  <span>{mapCashMovementTypeToText[data.direction]}</span>
                  {(data?.transaction?.type ||
                    data?.dividend_event?.type ||
                    data?.portfolio_transaction?.transaction_type) && (
                    <span className="text-gray-500 text-xs">
                      (
                      {data?.portfolio_transaction?.transaction_type
                        ? mapPortfolioTransactionType[data?.portfolio_transaction?.transaction_type]
                        : fixNameEnum(
                            data?.transaction?.type ||
                              getRegisterTypeStrings('Dividend', { capitalize: true }),
                          )}
                      )
                    </span>
                  )}
                </span>
              ),
            },
            {
              cell: (
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  {data?.from_bank ? (
                    <>
                      <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                        <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                          {data?.from_bank?.bank_account_name}
                        </span>
                        {/* <span className="text-gray-500"> */}
                        {/*   ({data?.from_bank?.bank_name.toUpperCase()}) */}
                        {/* </span> */}
                      </span>
                      <span className="text-gray-400">
                        {data?.from_bank?.bank_currency.toUpperCase()}
                      </span>
                    </>
                  ) : data?.shareholder_entity?.entity_name ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_entity?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_entity?.id,
                          },
                        )}
                      >
                        {data.shareholder_entity?.entity_name ?? '-'}
                      </div>
                    </>
                  ) : data?.shareholder_user ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_user?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_user?.id,
                          },
                        )}
                      >
                        {data?.shareholder_user?.first_name} {data?.shareholder_user?.last_name}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </span>
              ),
            },
            {
              cell: (
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  {data?.to_bank ? (
                    <>
                      <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                        <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                          {data?.to_bank?.bank_account_name}
                        </span>
                        {/* <span className="text-gray-500"> */}
                        {/*   ({data?.to_bank?.bank_name.toUpperCase()}) */}
                        {/* </span> */}
                      </span>
                      <span className="text-gray-400">
                        {data?.to_bank?.bank_currency.toUpperCase()}
                      </span>
                    </>
                  ) : data?.shareholder_entity?.entity_name ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_entity?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_entity?.id,
                          },
                        )}
                      >
                        {data.shareholder_entity?.entity_name ?? '-'}
                      </div>
                    </>
                  ) : data?.shareholder_user ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_user?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_user?.id,
                          },
                        )}
                      >
                        {data?.shareholder_user?.first_name} {data?.shareholder_user?.last_name}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </span>
              ),
            },
            {
              cell: (
                <span className="whitespace-nowrap text-nowrap">
                  {mapStatusToPlusMinusType[data.direction]}
                  {goodFormatNumberCustom(data.amount, currency)}
                </span>
              ),
            },
            {
              cell: (
                <div className="">
                  {dateToPrint(
                    data.transaction?.issue_date ||
                      data.dividend_event?.date ||
                      data?.portfolio_transaction?.created_at ||
                      data?.confirmation_date,
                    DateVariant.Table,
                    false,
                  ) || '-'}
                </div>
              ),
            },
          ];

          return {
            cells: body,
            onClick: () => handleOpenOperationInfoModal(data),
          };
        }),
      );
    });

    return result;
  }, [
    getRegisterTypeStrings,
    handleGoToHolderPage,
    handleOpenOperationInfoModal,
    issuerPaymentsList,
  ]);

  const dateValue =
    filters.requested_at_date_from && filters.requested_at_date_to
      ? ([dayjs(filters.requested_at_date_from), dayjs(filters.requested_at_date_to)] as [
          Dayjs,
          Dayjs,
        ])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              requested_at_date_from: date[0].toISOString(),
              requested_at_date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['requested_at_date_from', 'requested_at_date_to']);
      });
    },
    [setFilters],
  );

  const handleChangeStatus = useCallback(
    (status: PaymentStatus | DividendStatus | TransactionStatus | null) => {
      setFilters(filters =>
        status ? ({ ...filters, status } as CashMovementsTabFilters) : omit(filters, 'status'),
      );
    },
    [setFilters],
  );

  const handleChangeBank = useCallback(
    (option: SelectPaginateOption<RegisterBankExtended> | null) => {
      setFilters(filters =>
        option ? { ...filters, bank_id: option?.value?.id } : omit(filters, 'bank_id'),
      );
    },
    [setFilters],
  );

  const handleAddPayment = useCallback(() => {
    openModal(IssuerAddCashMovementModal, {
      portfolioId,
    });
  }, [openModal, portfolioId]);

  const optionsStatus = useMemo<
    SelectOption<PaymentStatus | DividendStatus | TransactionStatus>[]
  >(() => {
    const combinedOptionsMap = new Map<
      string | number,
      { value: string | number; label: string }
    >();

    const options = [
      ...getSelectOptionByEnum<PaymentStatus>(PaymentStatus),
      ...getSelectOptionByEnum<TransactionStatus>(TransactionStatus),
      ...getSelectOptionByEnum<DividendStatus>(DividendStatus),
    ];
    options.forEach(item => {
      const cleanLabel = item.label?.replace('Unsuccessfull', 'Unsuccessful');
      if (!combinedOptionsMap.has(item.value)) {
        combinedOptionsMap.set(item.value, { ...item, label: cleanLabel });
      }
    });

    const uniqueOptions = Array.from(combinedOptionsMap.values());

    return uniqueOptions as SelectOption<PaymentStatus | DividendStatus | TransactionStatus>[];
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full">
          <FilterItem
            placeholder="Status"
            type="select"
            value={filters.status}
            options={optionsStatus}
            onChange={handleChangeStatus}
            className="rounded-lg"
          />
          <FilterItem type="custom" className="rounded-lg" classes={{ element: 'py-0' }}>
            <RegisterBanksSelect
              onChange={handleChangeBank}
              value={filters.bank_id}
              placeholder="Bank"
              clearable={true}
              showBorderBottom={false}
              className="w-[370px]"
              prefillBank={false}
              hideType={true}
              is_overall={!portfolioId}
              portfolio_id={portfolioId}
            />
          </FilterItem>
          <FilterItem
            placeholder="Date"
            type="date"
            onChange={handleChangeDate}
            value={dateValue}
            className="rounded-lg"
          />
          {isFetching && <Spinner size="md" variant="primary" />}
        </div>
        {/* <TransactionsHeaderAction */}
        {/*   buttonName="Add new payment" */}
        {/*   openTypeTransaction="Payment" */}
        {/*   className="w-[170px]" */}
        {/*   fromPortfolio={true} */}
        {/* /> */}
        <div className="flex gap-2">
          <Button className="w-[170px]" onClick={handleAddPayment}>
            Add cash movement
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};
